/**
 * HomeScreen — главная Рублокса, витрина игр.
 *
 * Заменяет FeedScreen (1023 строки, 14 сетевых запросов при открытии,
 * 15 блоков подряд: WebGL-баннер, промо алмазов, промо Маськи, бегущая
 * строка, пьедестал, live-активность, тренды, топ авторов…).
 *
 * Три задачи, в порядке важности:
 *   1. вернуть человека к тому, во что он играл;
 *   2. показать, во что играют другие;
 *   3. дать быстрый вход в каталог.
 *
 * Запросов — четыре вместо четырнадцати. Поиск и фильтры в /app/games.
 *
 * При переводе на дизайн-систему:
 *  • у каждой полки СВОЙ флаг загрузки. Был один общий: «Продолжить»
 *    показывала скелетоны, пока грузились чужие подборки, а у гостя
 *    мигала вхолостую и исчезала — экран выглядел дёргающимся;
 *  • карточки в ленте были 184px шириной. При обложке 16/9 это 184×104 —
 *    нечитаемо. Теперь 260px, как в каталоге: карточка везде одна и та же;
 *  • свои .rb-btn и самодельные статы заменены компонентами UI-слоя.
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../context/AuthContext";
import { useCabinet } from "../../../context/CabinetContext";
import * as cabinetApi from "../../../api/cabinet";
import GameCard, { GameCardSkeleton } from "../../game/GameCard";
import CabIcon from "../CabIcon";
import { Button, Card, fmtNum } from "../../ui";
import "../../../styles/cabinet-home.css";

/**
 * Горизонтальная подборка.
 *
 * Пустая и загруженная — не рендерится вовсе: пустая полка с заголовком
 * это обещание, которое страница не выполнила. А вот пустая и ЗАГРУЖАЮЩАЯСЯ
 * рендерится скелетонами, иначе блоки прыгают по мере ответов сервера.
 */
function Shelf({ title, icon, games, loading, onOpen, onMore }) {
  if (!loading && (!games || games.length === 0)) return null;

  return (
    <section className="rb-home__shelf">
      <div className="rb-home__shelf-head">
        <h2 className="rb-home__shelf-title">
          {icon && <CabIcon name={icon} size={19} />} {title}
        </h2>
        {onMore && !loading && (
          <Button variant="link" size="sm" onClick={onMore}>
            Все игры
          </Button>
        )}
      </div>

      {/* Горизонтальная лента вместо сетки: подборка не должна занимать
          пол-экрана, а прокрутка вбок читается как «здесь ещё есть». */}
      <div className="rb-home__rail">
        {loading
          ? Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="rb-home__rail-item">
                <GameCardSkeleton />
              </div>
            ))
          : games.map((g) => (
              <div key={g.id} className="rb-home__rail-item">
                <GameCard game={g} onClick={onOpen} />
              </div>
            ))}
      </div>
    </section>
  );
}

export default function HomeScreen() {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const { diamonds, stars } = useCabinet();

  const [continueGames, setContinueGames] = useState([]);
  const [hits, setHits] = useState([]);
  const [fresh, setFresh] = useState([]);
  const [online, setOnline] = useState([]);

  /* Отдельный флаг на полку. Общий флаг заставлял все четыре ленты ждать
     самый медленный запрос — быстрые подборки простаивали в скелетонах. */
  const [busy, setBusy] = useState({
    hits: true,
    fresh: true,
    online: true,
    cont: false,
  });
  const done = (key) => setBusy((b) => ({ ...b, [key]: false }));

  useEffect(() => {
    let cancelled = false;
    const ctrl = new AbortController();
    const guard = (fn) => (r) => {
      if (!cancelled) fn(r);
    };

    // Каждый запрос со своим catch: если один упал, страница показывает
    // остальные, а не ломается целиком.
    cabinetApi
      .getFeedPage({ page: 1, perPage: 12, tab: "popular" }, ctrl.signal)
      .then(guard((r) => setHits(r?.projects || [])))
      .catch(() => {})
      .finally(() => !cancelled && done("hits"));

    cabinetApi
      .getFeedPage({ page: 1, perPage: 12, tab: "new" }, ctrl.signal)
      .then(guard((r) => setFresh(r?.projects || [])))
      .catch(() => {})
      .finally(() => !cancelled && done("fresh"));

    cabinetApi
      .getFeedPage({ page: 1, perPage: 24, tab: "recommended" }, ctrl.signal)
      // Мультиплеер бэкенд фильтровать не умеет (проверено: параметр
      // игнорируется), поэтому отбираем на клиенте из общей выборки.
      .then(guard((r) => setOnline((r?.projects || []).filter((g) => g.multiplayer).slice(0, 12))))
      .catch(() => {})
      .finally(() => !cancelled && done("online"));

    if (isAuthenticated && user?.userId) {
      setBusy((b) => ({ ...b, cont: true }));
      cabinetApi
        .getPlayHistory(user.userId, 12, ctrl.signal)
        .then(guard((r) => setContinueGames(Array.isArray(r) ? r : r?.projects || [])))
        .catch(() => {})
        .finally(() => !cancelled && done("cont"));
    }

    return () => {
      cancelled = true;
      ctrl.abort();
    };
  }, [isAuthenticated, user?.userId]);

  const open = (id) => navigate(`/app/game/${id}`);
  const toCatalog = () => navigate("/app/games");

  const name = user?.username;
  const hasHistory = continueGames.length > 0;

  return (
    <div className="ui-page">
      {/* Приветствие вместо WebGL-баннера: обращение по имени работает
          лучше анимации и грузится мгновенно. */}
      <section className="rb-home__hero">
        <div className="rb-home__hero-text">
          <h1 className="rb-home__hello">
            {name ? `Привет, ${name}!` : "Играй и создавай"}
          </h1>
          <p className="rb-home__lead">
            {hasHistory
              ? "Продолжай с того места, где остановился — или найди что-то новое."
              : "Больше сотни игр от таких же ребят. Заходи и играй."}
          </p>
          <div className="rb-home__cta">
            <Button
              variant="primary"
              onClick={toCatalog}
              icon={<CabIcon name="gamepad" size={17} />}
            >
              В каталог
            </Button>
            <Button
              variant="ghost"
              onClick={() => navigate("/app/my")}
              icon={<CabIcon name="puzzle" size={17} />}
            >
              Создать игру
            </Button>
          </div>
        </div>

        {isAuthenticated && (
          <div className="rb-home__stats">
            <Card
              hover
              className="rb-home__stat"
              onClick={() => navigate("/app/wallet")}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === "Enter" && navigate("/app/wallet")}
            >
              <CabIcon name="diamond" size={19} />
              <span className="rb-home__stat-val">{fmtNum(diamonds)}</span>
              <span className="rb-home__stat-cap">алмазов</span>
            </Card>
            <Card
              hover
              className="rb-home__stat"
              onClick={() => navigate("/app/wallet")}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === "Enter" && navigate("/app/wallet")}
            >
              <CabIcon name="star" size={19} />
              <span className="rb-home__stat-val">{fmtNum(stars)}</span>
              <span className="rb-home__stat-cap">звёзд</span>
            </Card>
          </div>
        )}
      </section>

      <Shelf
        title="Продолжить"
        icon="play"
        games={continueGames}
        loading={busy.cont}
        onOpen={open}
      />
      <Shelf
        title="Во что играют"
        icon="fire"
        games={hits}
        loading={busy.hits}
        onOpen={open}
        onMore={toCatalog}
      />
      <Shelf
        title="Играть вместе"
        icon="users"
        games={online}
        loading={busy.online}
        onOpen={open}
        onMore={toCatalog}
      />
      <Shelf
        title="Новинки"
        icon="sparkles"
        games={fresh}
        loading={busy.fresh}
        onOpen={open}
        onMore={toCatalog}
      />

      {/* Приземление после подборок: человек досмотрел — предлагаем
          следующий шаг, а не обрыв страницы. */}
      <Card className="rb-home__foot">
        <CabIcon name="gamepad" size={30} />
        <div className="rb-home__foot-text">
          <div className="rb-home__foot-title">Это не всё</div>
          <div className="rb-home__foot-sub">
            В каталоге больше сотни игр — с фильтрами по жанру и возрасту.
          </div>
        </div>
        <Button variant="primary" onClick={toCatalog}>
          Открыть каталог
        </Button>
      </Card>
    </div>
  );
}
